import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { registerSW } from 'virtual:pwa-register';
import { startEngine } from './sync/engine';
import { applyTheme, getInitialTheme } from './lib/theme';
import './index.css';

// TanStack Query — used for query caches (sites/wells lists). The
// SOURCE-OF-TRUTH for offline mutations is Dexie (pending_submissions
// table); TanStack mutation persistence is intentionally NOT used.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Long stale times — when offline, every fetch fails; when online,
      // we sync on a schedule, not on every render.
      staleTime: 5 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Service worker registration. `prompt` mode means the SW won't auto-
// activate until the user opts in via the "update available" toast.
const updateSW = registerSW({
  onNeedRefresh() {
    // App.tsx watches a global event for this; emit a message it can
    // pick up via window.dispatchEvent.
    window.dispatchEvent(new CustomEvent('argos:sw-update-available'));
  },
  onOfflineReady() {
    // No-op for now. We could surface "ready offline" toast here.
  },
});

// Expose for the update banner to call when the user accepts.
declare global {
  interface Window {
    __argosUpdateSW?: (reload?: boolean) => Promise<void>;
  }
}
window.__argosUpdateSW = updateSW;

// Apply the persisted theme before the first render so there's no flash.
applyTheme(getInitialTheme());

// Start the offline-first sync engine (drains pending_submissions on
// online / focus / visibilitychange / 30s heartbeat). Idempotent.
startEngine();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
